import React from 'react';
import { useParams } from 'react-router-dom';
import { SideBar } from '../../sidebar';
import i18n from '../../../../i18n';
import HeaderBanner from '../../../common/HeaderBanner';
import { formatDate } from '../../../../utils/common';
import { StatusNewBadge } from '../../../common/StatusBadge';
import { ArrowLeft, Expand, Shrink, ImageOff } from 'lucide-react';
import '../../../../styles/components/create-group.scss';
import { getGroupRequestDetail, approveGroupRequest } from './services/groupRequestService';
import { getCollectionTypes, getPaymentTypes } from './services/groupServices';

// NOTE: add these to constants/status.js next to AUCTION_REQUEST_STATUS /
// AUCTION_REQUEST_TYPE if you want them shared — same { text, color }
// shape StatusNewBadge expects. Kept local here for now, same reasoning
// as the auction version: better to have them defined somewhere than
// silently undefined.
const GROUP_REQUEST_STATUS = {
  pending: { text: 'Pending', color: '#f59e0b' },
  approved: { text: 'Approved', color: '#22c55e' },
  rejected: { text: 'Rejected', color: '#ef4444' },
  synced: { text: 'Synced', color: '#3b82f6' },
};
const GROUP_REQUEST_TYPE = {
  create: { text: 'Create', color: '#3b82f6' },
  update: { text: 'Update', color: '#f59e0b' },
  delete: { text: 'Delete', color: '#ef4444' },
};

const APPROVE_FORM_DEFAULTS = {
  collection_type: '',
  is_client_enrollment: false,
  is_auctions_grouped: false,
  is_grouped_enroll: false,
  payment_type: [], // array of payment_type IDs, same shape as GroupCreate.jsx
  focal_name: '',
  client_name: '',
  mobile: '',
  email: '',
};

class GroupRequestViewClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      request: null,
      isloading: true,
      notFound: false,
      hideSidebar: false,

      // --- inline approve panel, no separate route/page ---
      showApproveForm: false,
      approving: false,
      approveError: null,
      approveForm: { ...APPROVE_FORM_DEFAULTS },
      fileTerms: null,
      filePaymentTerms: null,
      collectionTypes: [],
      paymentTypes: [],
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.fetchDetail();
    this.loadCollectionTypes();
    this.loadPaymentTypes();
  }

  async loadCollectionTypes() {
    try {
      const response = await getCollectionTypes();
      this.setState({ collectionTypes: response.data || [] });
    } catch (error) {
      console.error('Error loading collection types:', error);
    }
  }

  async loadPaymentTypes() {
    try {
      const response = await getPaymentTypes();
      this.setState({ paymentTypes: response.data || [] });
    } catch (error) {
      console.error('Error loading payment types:', error);
    }
  }

  async fetchDetail() {
    const routeParams = this.props.params || {};
    const ymsGroupId = routeParams.yms_group_id ?? Object.values(routeParams)[0];

    if (!ymsGroupId) {
      console.error('GroupRequestView: no route param found. this.props.params =', routeParams);
      this.setState({ isloading: false, notFound: true });
      return;
    }

    this.setState({ isloading: true });
    try {
      const response = await getGroupRequestDetail(ymsGroupId);
      if (response?.success) {
        this.setState({ request: response.data, notFound: false });
      } else {
        this.setState({ notFound: true });
      }
    } catch (error) {
      console.error('Group request detail fetch failed:', error);
      this.setState({ notFound: true });
    } finally {
      this.setState({ isloading: false });
    }
  }

  renderField(label, value) {
    return (
      <div className="form-field">
        <label>{label}</label>
        <div className="view-field-value">{value ?? value === 0 ? value : '-'}</div>
      </div>
    );
  }

  renderCoverImage(request) {
    const url = request.image_url_full || request.url || request.image_url;

    if (!url) {
      return (
        <div className="attachment-empty">
          <ImageOff size={16} />
          <span>{i18n.t('no_image') || 'No image'}</span>
        </div>
      );
    }

    return (
      <img
        src={url}
        alt=""
        style={{
          width: '220px',
          height: '150px',
          objectFit: 'cover',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
        }}
      />
    );
  }

  handleApproveFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prev) => ({
      approveForm: {
        ...prev.approveForm,
        [name]: type === 'checkbox' ? checked : value,
      },
    }));
  };

  handleApproveFileChange = (e) => {
    const { name, files } = e.target;
    this.setState({ [name]: files && files[0] ? files[0] : null });
  };

  // Same pattern as GroupCreate.jsx's handleToggle, scoped to approveForm.
  handleApproveToggle = (field) => {
    this.setState((prev) => ({
      approveForm: { ...prev.approveForm, [field]: !prev.approveForm[field] },
    }));
  };

  // Same pattern as GroupCreate.jsx's handleCheckboxChange — payment_type
  // is an array of numeric IDs, not a comma string.
  handleApprovePaymentTypeChange = (e) => {
    const value = Number(e.target.value);
    const { checked } = e.target;

    this.setState((prev) => {
      let payment_type = [...prev.approveForm.payment_type];
      if (checked) {
        payment_type.push(value);
      } else {
        payment_type = payment_type.filter((item) => item !== value);
      }
      return { approveForm: { ...prev.approveForm, payment_type } };
    });
  };

  handleApproveSubmit = async () => {
    const { request, approveForm, fileTerms, filePaymentTerms } = this.state;

    if (!fileTerms || !filePaymentTerms) {
      this.setState({ approveError: 'Both terms files are required to approve.' });
      return;
    }
    if (!approveForm.collection_type) {
      this.setState({ approveError: 'Collection type is required.' });
      return;
    }
    if (!approveForm.payment_type || approveForm.payment_type.length === 0) {
      this.setState({ approveError: 'At least one payment type is required.' });
      return;
    }

    this.setState({ approving: true, approveError: null });

    try {
      const formData = new FormData();
      formData.append('collection_type', approveForm.collection_type);
      formData.append('is_client_enrollment', approveForm.is_client_enrollment ? '1' : '0');
      formData.append('is_auctions_grouped', approveForm.is_auctions_grouped ? '1' : '0');
      formData.append('is_grouped_enroll', approveForm.is_grouped_enroll ? '1' : '0');

      approveForm.payment_type.forEach((id, index) => {
        formData.append(`payment_type[${index}]`, id);
      });

      formData.append('client[focal_name]', approveForm.focal_name);
      formData.append('client[client_name]', approveForm.client_name);
      formData.append('client[mobile]', approveForm.mobile);
      formData.append('client[email]', approveForm.email);

      formData.append('files[file_terms]', fileTerms);
      formData.append('files[file_payment_terms]', filePaymentTerms);

      const response = await approveGroupRequest(request.public_id, formData);

      if (response?.success) {
        this.setState({
          showApproveForm: false,
          approveForm: { ...APPROVE_FORM_DEFAULTS },
          fileTerms: null,
          filePaymentTerms: null,
        });
        // Re-fetch so the badge/status on this page reflects 'synced'
        // immediately instead of waiting for a manual refresh.
        this.fetchDetail();
      } else {
        this.setState({ approveError: response?.message || 'Approval failed.' });
      }
    } catch (error) {
      console.error('Group request approval failed:', error);
      this.setState({
        approveError:
          error?.response?.data?.message || error?.message || 'Approval failed. Check the form and try again.',
      });
    } finally {
      this.setState({ approving: false });
    }
  };

  renderApprovePanel() {
    const { approveForm, approving, approveError, fileTerms, filePaymentTerms, collectionTypes, paymentTypes } = this.state;
    const isArabic = i18n.language === 'ar';

    return (
      <div className="form-section" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginTop: '16px' }}>
        <h2>{i18n.t('approve_group_request') || 'Approve & Create Group'}</h2>
        <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>
          {i18n.t('approve_group_request_hint') ||
            "YMS doesn't send these fields — fill them in to create the live group."}
        </p>

        {approveError && (
          <div className="attachment-empty" style={{ color: '#ef4444', marginBottom: '12px' }}>
            {approveError}
          </div>
        )}

        <div className="form-row">
          <div className="form-field">
            <label>{i18n.t('collection_type') || 'Collection Type'}</label>
            <select
              name="collection_type"
              value={approveForm.collection_type}
              onChange={this.handleApproveFieldChange}
              className="filter-base"
            >
              <option value="">{i18n.t('select_collection_type') || 'Select collection type'}</option>
              {collectionTypes.map((item) => (
                <option key={item.id} value={item.id}>
                  {isArabic ? item.name_ar : item.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-field form-field-margin-bottom">
          <label>{i18n.t('payment_type') || 'Payment Type'}</label>
          <div className="payment-type-group">
            {paymentTypes.map((item) => (
              <label className="payment-checkbox" key={item.id}>
                <input
                  type="checkbox"
                  name="payment_type"
                  value={item.id}
                  checked={approveForm.payment_type.includes(item.id)}
                  onChange={this.handleApprovePaymentTypeChange}
                />
                <span>{isArabic ? item.name_ar : item.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Same toggle markup/class as GroupCreate.jsx's "Configurations &
            Uploads" section — styled via create-group.scss, already
            imported at the top of this file. */}
        <div className="toggle-field">
          <label>{i18n.t('client_enrollment') || 'Client Enrollment'}</label>
          <div
            className={`toggle ${approveForm.is_client_enrollment ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_client_enrollment')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        <div className="toggle-field">
          <label>{i18n.t('grouped_auction') || 'Auctions Grouped'}</label>
          <div
            className={`toggle ${approveForm.is_auctions_grouped ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_auctions_grouped')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>
        <div className="toggle-field">
          <label>{i18n.t('grouped_enroll') || 'Grouped Enroll'}</label>
          <div
            className={`toggle ${approveForm.is_grouped_enroll ? 'active' : ''}`}
            onClick={() => this.handleApproveToggle('is_grouped_enroll')}
          >
            <div className="toggle-slider"></div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>{i18n.t('client_focal_name') || 'Client Focal Name'}</label>
            <input type="text" name="focal_name" value={approveForm.focal_name} onChange={this.handleApproveFieldChange} className="filter-base" />
          </div>
          <div className="form-field">
            <label>{i18n.t('client_name') || 'Client Name'}</label>
            <input type="text" name="client_name" value={approveForm.client_name} onChange={this.handleApproveFieldChange} className="filter-base" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>{i18n.t('client_mobile') || 'Client Mobile'}</label>
            <input type="text" name="mobile" value={approveForm.mobile} onChange={this.handleApproveFieldChange} className="filter-base" />
          </div>
          <div className="form-field">
            <label>{i18n.t('client_email') || 'Client Email'}</label>
            <input type="email" name="email" value={approveForm.email} onChange={this.handleApproveFieldChange} className="filter-base" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>{i18n.t('file_terms') || 'Terms File (pdf/doc/docx)'}</label>
            <input type="file" name="fileTerms" accept=".pdf,.doc,.docx" onChange={this.handleApproveFileChange} />
            {fileTerms && <span style={{ fontSize: '0.8rem' }}>{fileTerms.name}</span>}
          </div>
          <div className="form-field">
            <label>{i18n.t('file_payment_terms') || 'Payment Terms File (pdf/doc/docx)'}</label>
            <input type="file" name="filePaymentTerms" accept=".pdf,.doc,.docx" onChange={this.handleApproveFileChange} />
            {filePaymentTerms && <span style={{ fontSize: '0.8rem' }}>{filePaymentTerms.name}</span>}
          </div>
        </div>

        <div className="form-row" style={{ marginTop: '12px' }}>
          <button
            className="button1 button1-gradient button1-action"
            disabled={approving}
            onClick={this.handleApproveSubmit}
          >
            {approving ? i18n.t('submitting') || 'Submitting...' : i18n.t('submit') || 'Submit & Create Group'}
          </button>
          <button
            className="button1 button1-outline button1-action"
            style={{ marginLeft: '8px' }}
            disabled={approving}
            onClick={() =>
              this.setState({
                showApproveForm: false,
                approveError: null,
                approveForm: { ...APPROVE_FORM_DEFAULTS },
                fileTerms: null,
                filePaymentTerms: null,
              })
            }
          >
            {i18n.t('cancel') || 'Cancel'}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { request, isloading, notFound, hideSidebar, showApproveForm } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('group_request_details') || 'Group Request Details'}
          subtitle={i18n.t('review_group_request_from_yms') || 'Details submitted by YMS for this group'}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
              <div
                className={hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !hideSidebar })}
                      >
                        {hideSidebar && <Shrink strokeWidth={1} />}
                        {!hideSidebar && <Expand strokeWidth={1} />}
                      </span>
                    </div>
                    <div className="header-action">
                      {/* Opens the inline panel below — no navigation, no
                          separate page/route. Hidden once already synced. */}
                      {request && request.status !== 'synced' && !showApproveForm && (
                        <button
                          className="button1 button1-gradient button1-action"
                          style={{ marginRight: '0.5rem' }}
                          onClick={() => this.setState({ showApproveForm: true })}
                        >
                          {i18n.t('approve') || 'Approve'}
                        </button>
                      )}
                      <button
                        className="button1 button1-gradient button1-back"
                        onClick={() => window.history.back()}
                      >
                        <ArrowLeft size={18} className="icon" />
                        <span>{i18n.t('back')}</span>
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    {isloading ? (
                      <div className="attachment-empty">{i18n.t('loading') || 'Loading...'}</div>
                    ) : notFound || !request ? (
                      <div className="attachment-empty">
                        {i18n.t('group_request_not_found') || 'Group request not found'}
                      </div>
                    ) : (
                      <div className="create-group-container">
                        <div className="form-section">
                          <h2>
                            {request.group_name || request.yms_group_id}{' '}
                            <StatusNewBadge status={GROUP_REQUEST_STATUS[request.status]} />{' '}
                            <StatusNewBadge status={GROUP_REQUEST_TYPE[request.request_type]} />
                          </h2>

                          <div className="form-row">
                            {this.renderField(i18n.t('yms_group_id') || 'YMS Group ID', request.yms_group_id)}
                            {this.renderField(i18n.t('project_code') || 'Project Code', request.project_code)}
                            {this.renderField('Group Name (EN)', request.group_name)}
                            {this.renderField('Group Name (AR)', request.group_name_ar)}
                          </div>

                          <div className="form-row">
                            {this.renderField(i18n.t('organization') || 'Organization', request.organization)}
                            {this.renderField(i18n.t('category') || 'Category', request.category_id)}
                            {this.renderField(i18n.t('sector') || 'Sector', request.sector_id)}
                            {this.renderField(i18n.t('amount') || 'Amount', request.amount)}
                          </div>

                          <div className="form-row">
                            {this.renderField(i18n.t('manager') || 'Manager', request.manager)}
                            {this.renderField(i18n.t('hse') || 'HSE Officer', request.hse)}
                            {this.renderField(
                              i18n.t('actual_start_date') || 'Actual Start Date',
                              request.actual_start_date ? formatDate(request.actual_start_date) : '-'
                            )}
                            {this.renderField(
                              i18n.t('expected_end_date') || 'Expected End Date',
                              request.expected_end_date ? formatDate(request.expected_end_date) : '-'
                            )}
                          </div>

                          <div className="form-row">
                            {this.renderField(i18n.t('start_date') || 'Bidding Start', request.start_date ? formatDate(request.start_date) : '-')}
                            {this.renderField(i18n.t('end_date') || 'Bidding End', request.end_date ? formatDate(request.end_date) : '-')}
                            {this.renderField(
                              i18n.t('reg_start_date') || 'Registration Start',
                              request.reg_start_date ? formatDate(request.reg_start_date) : '-'
                            )}
                            {this.renderField(
                              i18n.t('reg_end_date') || 'Registration End',
                              request.reg_end_date ? formatDate(request.reg_end_date) : '-'
                            )}
                          </div>

                          <div className="form-row">
                            {this.renderField('Description (EN)', request.description)}
                            {this.renderField('Description (AR)', request.description_ar)}
                            {this.renderField(i18n.t('submitted_on') || 'Submitted On', request.created_at ? formatDate(request.created_at) : '-')}
                            {this.renderField(i18n.t('last_updated') || 'Last Updated', request.updated_at ? formatDate(request.updated_at) : '-')}
                          </div>

                          {request.reason && (
                            <div className="form-row">
                              {this.renderField(i18n.t('reason') || 'Reason', request.reason)}
                            </div>
                          )}
                        </div>

                        <div className="form-section">
                          <h2>{i18n.t('cover_image') || 'Cover Image'}</h2>
                          {this.renderCoverImage(request)}
                        </div>

                        {showApproveForm && this.renderApprovePanel()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

// Export the useParams() wrapper under the name GroupRequestView so
// existing import/route usage (`import { GroupRequestView } from ...`)
// doesn't need to change, while still getting the route param correctly
// regardless of what it's named in your <Route path="...">.
export function GroupRequestView(props) {
  const params = useParams();
  return <GroupRequestViewClass {...props} params={params} />;
}